import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { supabase, USER_ROLES } from "../lib/supabase";

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(USER_ROLES.GENERAL);
  const [hasRequestedRole, setHasRequestedRole] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Memoized fetch function to prevent unnecessary re-calls
  const fetchUserRole = useCallback(
    async (userEmail) => {
      if (!userEmail || loading) return;

      try {
        setLoading(true);

        // SECURITY FIX: Use RPC function instead of direct query to avoid exposing email in URL
        const { data, error } = await supabase.rpc("get_user_role_secure", {
          user_email: userEmail,
        });

        if (error && error.code !== "PGRST116") {
          throw error;
        }

        // RPC function returns an array, so get the first result
        const userData = data && data.length > 0 ? data[0] : null;

        if (userData) {
          setUserRole(userData.user_role || USER_ROLES.GENERAL);
          setHasRequestedRole(userData.has_requested_role || false);
        } else {
          setUserRole(USER_ROLES.GENERAL);
          setHasRequestedRole(false);
        }

        setInitialized(true);
      } catch (error) {
        console.error("Error fetching user role:", error);
        setUserRole(USER_ROLES.GENERAL);
        setHasRequestedRole(false);
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    },
    [loading],
  );

  // Effect to fetch role when user changes
  useEffect(() => {
    if (user?.email && !initialized) {
      fetchUserRole(user.email);
    } else if (!user?.email) {
      setUserRole(USER_ROLES.GENERAL);
      setHasRequestedRole(false);
      setInitialized(false);
    }
  }, [user?.email, initialized, fetchUserRole]);

  // Function to refresh role data
  const refreshRole = useCallback(() => {
    if (user?.email) {
      setInitialized(false);
      fetchUserRole(user.email);
    }
  }, [user?.email, fetchUserRole]);

  const value = {
    userRole,
    hasRequestedRole,
    loading,
    initialized,
    refreshRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
